import { WillMyCreditsTransferState } from '@/store/wmct/state';
import { ICourseInfo, IInstitutionInfo } from '@/models';
import { IEditable } from '@/models/IEditable';
import CourseEntry from '@/models/CourseEntry';
import {
	IPersistResult,
	IWillMyCreditsTransferResponse,
	IWillMyCreditsTransferCheckInResponse,
} from '@/models/services/collegetransfer/entities';
import { AssessmentEntry } from '@/models';
import LearningEntry from '@/models/LearningEntry';
import { IValidator } from '@/models/validation/IValidator';

type addLearningCallback<T extends LearningEntry> = (newLearning: T, lastEntered: T) => void;

function addLearning<T extends LearningEntry>(col: T[], learning: T, cb?: addLearningCallback<T>) {
	if (col.length) {
		const last = col[col.length - 1];

		learning.institution.label = last.institution.label;
		learning.institution.value = last.institution.value;
		learning.institution.logo = last.institution.logo;
		learning.institution.institutionId = last.institution.institutionId;
		learning.institution.scorePolicy = last.institution.scorePolicy;

		learning.academicYear = last.academicYear;
		if (cb) {
			cb(learning, last);
		}
	}

	col.push(learning);
}

function removeLearning<T extends LearningEntry>(col: T[], learning: T) {
	const index = col.indexOf(learning);
	const name = typeof learning;

	if (index < 0) {
		throw new Error(`${name} not found in state and cannot be removed: ${learning}`);
	}

	col.splice(index, 1);
}

const mutations = {
	UPDATE_API_STATUS_BEGIN(state: WillMyCreditsTransferState) {
		state.api.loading = true;
		state.api.error = false;
	},

	UPDATE_API_STATUS_END(state: WillMyCreditsTransferState, { result, error }
		: { result: IWillMyCreditsTransferCheckInResponse, error: Error }) {
		state.api.error = error != null;
		state.api.loading = false;

		if (result != null) {
			state.api.data = {
				authorized: result.Authorized,
				institutionName:  result.InstitutionName,
				providers: result.AssessmentProviders,
				nextStepsText: result.NextStepsText,
			};
		}
	},

	RESET(state: WillMyCreditsTransferState) {
		state.courses = [];
		state.assessments = [];
		state.equivalencyMap.Transcript = null;
		state.equivalencyMap.TransferMap = null;
	},

	ADD_COURSE(state: WillMyCreditsTransferState) {
		addLearning(state.courses, new CourseEntry(), (c, last) => c.academicTerm = last.academicTerm);
	},

	ADD_ASSESSMENT(state: WillMyCreditsTransferState) {
		addLearning(state.assessments, new AssessmentEntry());
	},

	EDIT_LEARNING(state: WillMyCreditsTransferState, { editable }: { editable: IEditable }) {
		editable.isEditing = !editable.isEditing;

		const isEditing = editable.isEditing === true;

		if (isEditing) {
			state.courses.forEach((course) => {
				if (course !== editable) {
					course.isEditing = false;
				}
			});

			state.assessments.forEach((assessment) => {
				if (assessment !== editable) {
					assessment.isEditing = false;
				}
			});
		}
	},

	REMOVE_COURSE(state: WillMyCreditsTransferState, { course }: { course: CourseEntry }) {
		removeLearning(state.courses, course);
	},

	REMOVE_ASSESSMENT(state: WillMyCreditsTransferState, { assessment }: { assessment: AssessmentEntry }) {
		removeLearning(state.assessments, assessment);
	},

	UPDATE_LEARNING_YEAR(state: WillMyCreditsTransferState, { learning, year }
		: { learning: LearningEntry, year: number }) {
		learning.academicYear = year;
	},

	UPDATE_COURSE_TERM(state: WillMyCreditsTransferState, { course, term }: { course: CourseEntry, term: number }) {
		course.academicTerm = term;
	},

	UPDATE_LEARNING_INSTITUTION(state: WillMyCreditsTransferState, { learning, inst }
		: { learning: LearningEntry, inst: IInstitutionInfo }) {
		learning.institution.institutionId = inst.institutionId;
		learning.institution.label = inst.label;
		learning.institution.value = inst.value;
		learning.institution.logo = inst.logo;
		learning.institution.scorePolicy = inst.scorePolicy;

		learning.course.institutionId = null;
		learning.course.label = null;
		learning.course.number = null;
		learning.course.prefix = null;
		learning.course.value = null;
	},

	UPDATE_LEARNING_COURSE(state: WillMyCreditsTransferState, { learning, selectedCourse }
		: { learning: LearningEntry, selectedCourse: ICourseInfo }) {
		learning.course.institutionId = selectedCourse.institutionId;
		learning.course.label = selectedCourse.label;
		learning.course.number = selectedCourse.number;
		learning.course.prefix = selectedCourse.prefix;
		learning.course.value = selectedCourse.value;
	},

	UPDATE_COURSE_GRADE(state: WillMyCreditsTransferState, { course, grade }
		: { course: CourseEntry, grade: string }) {
		course.grade = grade;
	},

	UPDATE_ASSESSMENT_SCORE(state: WillMyCreditsTransferState, { assessment, score }
		: { assessment: AssessmentEntry, score: string | null }) {
		assessment.score = score;
	},

	BEGIN_LEARNING_PERISTING(state: WillMyCreditsTransferState, { learning, validator }
		: { learning: LearningEntry, validator: IValidator<LearningEntry> }) {
		const isValid = validator.isValid(learning);

		learning.isValidating = !isValid;
		learning.isPersisting = isValid;
	},

	END_LEARNING_PERSISTING(state: WillMyCreditsTransferState, { learning, persistResult }:
		{ learning: LearningEntry, persistResult: IPersistResult }) {
		if (learning.id !== persistResult.entity) {
			learning.id = persistResult.entity;
		}

		learning.isEditing = false;
		learning.isPersisting = false;
	},

	BEGIN_EQUIV_MAP_UPDATE(state: WillMyCreditsTransferState) {
		state.equivalencyMapLoading = true;
		state.equivalencyMapError = false;
	},

	END_EQUIV_MAP_UPDATE(state: WillMyCreditsTransferState, { wmctResponse, err }
		: { wmctResponse: IWillMyCreditsTransferResponse, err: Error }) {
		state.equivalencyMap = wmctResponse;
		state.equivalencyMapLoading = false;
		state.equivalencyMapError = err != null;
	},

	UPDATE_SEEN_EQUIVS(state: WillMyCreditsTransferState, seen: number) {
		state.seenEquivalencies = seen;
	},
};

export default mutations;
